<template>
  <div id="tour-student-overview-page" class="flex flex-col h-screen overflow-hidden">

    <!-- Back + Actions -->
    <div class="flex items-center justify-between px-3 py-2 bg-white flex-shrink-0 border-b border-gray-200">
      <div class="flex items-center">
        <Button icon="pi pi-arrow-left text-black" text rounded size="small" @click="goBack" />
        <span class="ml-1.5 text-sm text-gray-800 font-medium">Back</span>
      </div>
      <div class="flex items-center gap-1.5">
        <Button label="CBT Report" icon="pi pi-file-pdf" class="p-button-sm p-button-secondary p-button-outlined"
          :disabled="!student"
          @click="showCbtReportDialog = true"
          data-testid="btn-student-cbt-report" />
        <Button v-if="canGenerateIdCard" label="ID Card" icon="pi pi-id-card"
          class="p-button-sm p-button-secondary p-button-outlined"
          :disabled="!student" :loading="generatingIdCard"
          @click="generateIdCard"
          data-testid="btn-student-id-card" />
        <Button label="Reset Password" icon="pi pi-lock" class="p-button-sm p-button-danger" @click="showDialog = true" />
      </div>
    </div>

    <!-- Scrollable Content -->
    <div class="flex-1 overflow-y-auto p-3 mobile-scroll">
      <div v-if="loading" class="flex justify-center items-center h-40">
        <Loader />
      </div>

      <div v-else-if="student" class="space-y-3 pb-5 ">
        <StudentPersonalInfo :student="student" @refresh="handleRefresh" />
        <StudentCustomFields :studentId="studentId" />
        <StudentClassroomInfo :student="student" />
        <StudentEnrollmentHistory v-if="isTertiary" :records="studentEnrollmentRecords" :studentId="studentId" @refresh="handleRefresh" />
        <StudentDisablementHistory :studentId="studentId" />
        <div  v-if="isTertiary">
          <!-- <StudentCourses :curriculum="studentCurriculum" /> -->

           <StudentCourseRegistrations
            :registrations="studentCourseRegistrations"
            :studentAcademicCalendar="studentAcademicCalendar"
            @refresh="handleRefresh"
          />

          <div class="mt-3">
            <h3 class="text-sm font-bold text-gray-800 mb-2">Academic Results</h3>
            <TertiaryStudentResult :studentId="studentId" :canManageResults="true" />
          </div>
        </div>

        <div  v-if="!isTertiary">
          <StudentSubjects :subjects="studentSubjects" :loading="subjectsLoading" @refresh="refetchSubjects" />
        </div>

        <StudentAttendance :student="student" />
        <StudentBills :bills="student.studentBills" :studentId="studentId" />
      </div>

      <div v-else class="text-center py-6 text-gray-500 text-sm">
        No student found.
      </div>
    </div>

    <!-- Reset Password Dialog -->
    <Dialog v-model:visible="showDialog" header="Reset Student Password" modal :closable="!resetting" :draggable="false"
      class="w-[90%] md:w-[400px]">
      <p class="text-sm text-gray-700 mb-2">
        Are you sure you want to reset this student's password?
      </p>
      <div class="flex justify-end gap-2">
        <Button label="Cancel" text size="small" @click="showDialog = false" :disabled="resetting" />
        <Button label="Yes, Reset" class="p-button-sm p-button-danger" :loading="resetting" @click="resetPassword" />
      </div>
    </Dialog>

    <!-- CBT Report Dialog -->
    <StudentCbtReportDialog
      v-if="student"
      v-model:visible="showCbtReportDialog"
      :studentId="String(studentId)"
      :studentLabel="`${student.first_name} ${student.surname}`"
    />
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { type StudentAcademicCalendar, type CurriculumType, type Student, type StudentCourseRegistration } from '@/types/composition-functions';
import { ADMIN_RESET_STUDENT_PASSWORD, GENERATE_STUDENT_ID_CARD, GET_STUDENT_SUBJECTS } from '@/graphql';
import { QUERY_STUDENT_BY_ID } from '@/graphql';
import Loader from '@/components/common/loader.vue';
import { useMutation, useQuery } from '@vue/apollo-composable';

import StudentPersonalInfo from './StudentPersonalInfo.vue';
import StudentCustomFields from './StudentCustomFields.vue';
import StudentClassroomInfo from './StudentClassroomInfo.vue';
import StudentDisablementHistory from './StudentDisablementHistory.vue';
import StudentSubjects from './StudentSubjects.vue';
import StudentBills from './StudentBills.vue';
import StudentAttendance from './StudentAttendance.vue';
import StudentCourses from './StudentCourses.vue';
import StudentCourseRegistrations from './StudentCourseRegistations.vue';
import StudentEnrollmentHistory from './StudentEnrollmentHistory.vue';
import StudentCbtReportDialog from '../StudentCbtReportDialog.vue';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { getErrorMessage } from '@/utils/error';

const toast = useToast();
const router = useRouter();
const toggleSidebar = inject<() => void>('toggleSidebar', () => { });
const route = useRoute();
const studentId = Number(route.params.id);

const { isTertiary } = useSchoolFeatures();

const student = ref<Student | null>(null);
const loading = ref(true);
const showDialog = ref(false);
const resetting = ref(false);
const showCbtReportDialog = ref(false);
const generatingIdCard = ref(false);

const userStore = useUserStore();
const canGenerateIdCard = computed(() =>
  can(userStore.currentUser, AppPermissions.IdCards.Actions.Generate, AppPermissions.IdCards.Resource),
);

const goBack = () => router.back();

const handleRefresh = async() => {
  loading.value = true;
  await refetchStudent();
  loading.value = false;
};

// Initialize query at setup level (synchronous) to avoid "useQuery outside active effect scope" warning
// We start with include_tertiary=false; when isTertiary changes, we refetch with updated variables
const { load: loadStudent, refetch: refetchStudentQuery } = QUERY_STUDENT_BY_ID({
  id: studentId,
  autoFetch: false,
  include_tertiary: isTertiary.value,
});

// Fetch Subjects
const { result: subjectsResult, loading: subjectsLoading, refetch: refetchSubjects } = useQuery(GET_STUDENT_SUBJECTS, { student_id: studentId });
const studentSubjects = computed(() => subjectsResult.value?.studentSubjects || []);

const refetchStudent = async() => {
  try {
    const result = await refetchStudentQuery({ id: studentId, include_tertiary: isTertiary.value });
    if (result?.data?.student) {
      student.value = result.data.student;
    }
  } catch {
    // Best-effort refetch; keep the currently displayed student on failure.
  }
};

const initializeStudent = async() => {
  try {
    loading.value = true;
    const result = await loadStudent();
    if (result && typeof result !== 'boolean' && result.student) {
      student.value = result.student;
    } else {
      toast.add({ severity: 'warn', summary: 'Not Found', detail: 'Student not found.', life: 3000 });
    }
  } catch {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Unable to load student details.', life: 3000 });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  initializeStudent();
});

// Refetch when isTertiary status is confirmed so we get tertiary-specific fields
watch(isTertiary, async(newValue: boolean) => {
  if (newValue && student.value) {
    loading.value = true;
    await refetchStudent();
    loading.value = false;
  }
});

const { mutate: resetStudentPassword } = useMutation(ADMIN_RESET_STUDENT_PASSWORD);

async function resetPassword() {
  resetting.value = true;
  try {
    const { data } = (await resetStudentPassword({
      input: { student_id: studentId },
    }))!;

    if (data?.adminResetStudentPassword?.success) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: data.adminResetStudentPassword.message,
        life: 3000,
      });
      showDialog.value = false;
    } else {
      toast.add({
        severity: 'error',
        summary: 'Failed',
        detail: data?.adminResetStudentPassword?.message || 'Unable to reset password',
        life: 3000,
      });
    }
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Something went wrong while resetting password.',
      life: 3000,
    });
  } finally {
    resetting.value = false;
  }
}

const { mutate: generateStudentIdCard } = useMutation(GENERATE_STUDENT_ID_CARD);

// The card is rendered by an export job, so the button only confirms the job
// started — the finished PDF arrives in the Downloads tray like every other
// export, and a second click while one is queued reuses the same export.
async function generateIdCard() {
  generatingIdCard.value = true;
  try {
    await generateStudentIdCard({ student_id: String(studentId) });
    toast.add({
      severity: 'success',
      summary: 'ID Card',
      detail: 'The ID card is being generated. Check the Downloads icon in the top bar.',
      life: 4000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Failed',
      detail: getErrorMessage(error, 'Unable to generate the ID card.'),
      life: 4000,
    });
  } finally {
    generatingIdCard.value = false;
  }
}

const studentCurriculum = computed<CurriculumType[]>(() =>
  student.value?.studentActiveCurriculum?.filter(
    (c): c is CurriculumType => c !== null,
  ) ?? [],
);

const studentCourseRegistrations = computed<StudentCourseRegistration[]>(() =>
  (student.value?.studentCourseRegistration ?? []).filter(
    (r): r is StudentCourseRegistration => r !== null,
  ),
);

const studentAcademicCalendar = computed<StudentAcademicCalendar | undefined>(() =>
  student.value?.studentAcademicCalendar ?? undefined,
);

const studentEnrollmentRecords = computed(() =>
  student.value?.studentAcademicCalendars ?? [],
);

</script>

<style scoped>
.mobile-scroll {
  -webkit-overflow-scrolling: touch;
}
</style>
